import { HeroIcon } from '@components/HeroIcon'
import { Modal } from '@components/Modal'
import { ProgressCircle } from '@components/ProgressCircle'
import { ScrollBar } from '@components/ScrollBar'
import { ContentsModalProvider } from '@contexts/ContentsModalContext'
import { useCourse } from '@hooks/useCourse'
import { useEnrollment } from '@hooks/useEnrollment'
import { useModules } from '@hooks/useModules'
import { useContentsModalStore } from '@stores/useContentsModalStore'
import { useFullScreenModeStore } from '@stores/useFullScreenMode'
import { useLocalSearchParams, usePathname } from 'expo-router'
import React, { useCallback, useMemo, useState } from 'react'
import { Text, XStack, YStack } from 'tamagui'
import { ContentsModules } from './ContentsModules'

export default function ContentsModal() {
  const { position, setPosition } = useContentsModalStore()
  const { modules, onLoadMore } = useModules()
  const { enrollment } = useEnrollment()
  const { course } = useCourse()
  const { moduleId, parentCourseModuleId } = useLocalSearchParams()
  const [scrollBarHeight, setScrollBarHeight] = useState(0)
  const { isFullScreenMode } = useFullScreenModeStore()
  const snapPoints = [85, 15]
  const isModalOpen = position === 0
  const modalChevronIcon = isModalOpen ? 'xmark' : 'chevron-up'

  const handleChange = useCallback(
    (index: number) => {
      setPosition(index)
    },
    [setPosition],
  )

  const togglePosition = useCallback(() => {
    setPosition(position === 0 ? 1 : 0)
  }, [position, setPosition])

  const setIsOpen = useCallback(
    (isOpen: number) => {
      setPosition(isOpen)
    },
    [setPosition],
  )

  const openModal = useMemo(() => {
    return !isFullScreenMode
  }, [isFullScreenMode])

  const filteredModules = useMemo(() => {
    if (!modules) return []
    return modules.nodes.filter((module) => module.available !== false)
  }, [modules])

  const renderModules = useMemo(() => {
    return filteredModules.map((module, index) => {
      const isModuleOpen = [moduleId, parentCourseModuleId].includes(
        module.id.toString(),
      )
      const isLastModule = index === filteredModules.length - 1
      return (
        <React.Fragment key={module.id}>
          <ContentsModules
            module={module}
            moduleId={moduleId}
            onLoadMore={onLoadMore}
            isLastModule={isLastModule}
            isModuleOpen={isModuleOpen}
            isSubmodule={false}
          />
        </React.Fragment>
      )
    })
  }, [filteredModules, moduleId, parentCourseModuleId, onLoadMore])

  const actualPath = usePathname()

  const isValidPath =
    /^\/enrollments\/\d+\/courses\/\d+\/online_course\/course_contents\/\d+$/.test(
      actualPath,
    )

  return (
    <>
      <ContentsModalProvider setIsOpen={setIsOpen} isOpen={isModalOpen}>
        <Modal
          open={isValidPath && openModal}
          defaultOpen={isValidPath && openModal}
          snapPoints={snapPoints}
          position={position}
          shouldAdapt={false}
          onPositionChange={handleChange}
          keepVisible={isValidPath && openModal}
          borderRadius="$border-radius-xlg"
          disableDrag
          hideOverlay
          withBorder
        >
          <XStack
            p="$spacing-inset-xs"
            gap="$spacing-inset-nano"
            alignItems="center"
            minHeight={100}
            onPress={togglePosition}
            role="button"
          >
            <ProgressCircle progress={enrollment?.progressCard.progress || 0} />
            <YStack flex={1} gap="$spacing-inset-quarck">
              <Text
                color="$neutral-800"
                fontSize="$font-size-xs"
                fontFamily="$mulishSemiBold"
                fontWeight="600"
                width="90%"
                numberOfLines={3}
              >
                {course?.name}
              </Text>
              <XStack gap="$spacing-inset-quarck">
                <Text
                  color="$success-300"
                  fontSize="$font-size-xxxs"
                  fontFamily="$mulishBold"
                  fontWeight="700"
                >
                  {Math.round(enrollment?.progressCard.progress || 0)}%
                </Text>
                <Text
                  color="$neutral-800"
                  fontSize="$font-size-xxxs"
                  fontFamily="$mulishBold"
                  fontWeight="700"
                >
                  |
                </Text>
                <Text
                  color="$success-300"
                  fontSize="$font-size-xxxs"
                  fontFamily="$mulishBold"
                  fontWeight="700"
                >
                  {enrollment?.progressCard.completed}
                </Text>
                <Text
                  color="$neutral-800"
                  fontSize="$font-size-xxxs"
                  fontFamily="$mulishRegular"
                >
                  de {enrollment?.progressCard.total} conteúdos
                </Text>
              </XStack>
            </YStack>
            <HeroIcon
              icon={['fal', modalChevronIcon]}
              size={16}
              color="$neutral-500"
            />
          </XStack>
          <YStack
            flex={1}
            backgroundColor="$neutral-0"
            paddingVertical="$spacing-inset-xs"
            marginHorizontal="$spacing-inset-xs"
            onLayout={(event) =>
              setScrollBarHeight(event.nativeEvent.layout.height)
            }
          >
            <ScrollBar
              maxHeight={scrollBarHeight}
              displayNativeIndicator={false}
              scrollBarId="contents-modal-scroll"
            >
              {renderModules}
            </ScrollBar>
          </YStack>
        </Modal>
      </ContentsModalProvider>
    </>
  )
}
